import { ShaderProgram } from '../renderer/ShaderProgram';
import type { Renderable } from '../renderer/RenderPipeline';
import type { Camera } from '../renderer/Camera';
import type { TravelConfig } from '../config/TravelConfig';
import { axialToWorld } from '../math/HexMath';
import { WarpSegment } from '../systems/TravelTypes';

import vertSrc from '../shaders/travel-line.vert.glsl?raw';
import fragSrc from '../shaders/travel-line.frag.glsl?raw';

const MAX_POINTS = 4096;
const FPV = 9;

/**
 * Renders a ribbon (TRIANGLE_STRIP) along the travel route.
 * Per vertex: [x, y, z, cumDist, r, g, b, warp, edge].
 * Normal segments: dashed + soft edges. Warp: solid glow.
 */
export class TravelLineRenderer implements Renderable {
  private _gl: WebGL2RenderingContext;
  private _config: TravelConfig;
  private _shader: ShaderProgram;
  private _vao: WebGLVertexArrayObject;
  private _vbo: WebGLBuffer;
  private _vertCount = 0;
  private _time = 0;
  private _visible = false;
  private _suppressed = false;

  private _buf = new Float32Array(MAX_POINTS * 2 * FPV);
  private _wxz = new Float32Array(MAX_POINTS * 2);

  constructor(gl: WebGL2RenderingContext, config: TravelConfig) {
    this._gl = gl;
    this._config = config;
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);

    this._vao = gl.createVertexArray()!;
    this._vbo = gl.createBuffer()!;

    gl.bindVertexArray(this._vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this._vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this._buf.byteLength, gl.DYNAMIC_DRAW);

    const stride = FPV * 4;
    this._attr('a_position', 3, stride, 0);
    this._attr('a_dist', 1, stride, 12);
    this._attr('a_color', 3, stride, 16);
    this._attr('a_warp', 1, stride, 28);
    this._attr('a_edge', 1, stride, 32);

    gl.bindVertexArray(null);
  }

  buildFromHexes(
    hexes: readonly { q: number; r: number }[],
    hexRadius: number,
    flightHeight: number,
    segmentKinds: Uint8Array | null,
    normalColor: readonly number[],
    warpColor: readonly number[],
  ): void {
    const count = Math.min(hexes.length, MAX_POINTS);
    if (count < 2) { this._visible = false; return; }

    for (let i = 0; i < count; i++) {
      const [wx, wz] = axialToWorld(hexes[i].q, hexes[i].r, hexRadius);
      this._wxz[i * 2] = wx;
      this._wxz[i * 2 + 1] = wz;
    }

    const ribbonY = flightHeight - 0.03;
    let cumDist = 0;

    for (let i = 0; i < count; i++) {
      const wx = this._wxz[i * 2], wz = this._wxz[i * 2 + 1];

      if (i > 0) {
        const dx = wx - this._wxz[(i - 1) * 2];
        const dz = wz - this._wxz[(i - 1) * 2 + 1];
        cumDist += Math.sqrt(dx * dx + dz * dz);
      }

      const [dirX, dirZ] = this._direction(i, count);

      const segIdx = Math.min(i, count - 2);
      const isWarp = segmentKinds !== null
        && segmentKinds[segIdx] === WarpSegment.Warp;
      const c = isWarp ? warpColor : normalColor;
      const hw = isWarp ? this._config.routeHalfWidthWarp : this._config.routeHalfWidth;

      const px = -dirZ * hw, pz = dirX * hw;

      this._writeVert(i * 2, wx + px, ribbonY, wz + pz, cumDist, c, isWarp, 0.0);
      this._writeVert(i * 2 + 1, wx - px, ribbonY, wz - pz, cumDist, c, isWarp, 1.0);
    }

    this._vertCount = count * 2;
    this._visible = true;

    const gl = this._gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this._vbo);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this._buf, 0, this._vertCount * FPV);
  }

  clear(): void { this._visible = false; this._vertCount = 0; }
  setSuppressed(v: boolean): void { this._suppressed = v; }
  advanceTime(dt: number): void { this._time += dt; }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (!this._visible || this._suppressed || this._vertCount < 4) return;

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setFloat('u_dashSize', 1.5);
    this._shader.setFloat('u_gapSize', 0.8);
    this._shader.setFloat('u_time', this._time);

    gl.bindVertexArray(this._vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, this._vertCount);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    this._gl.deleteBuffer(this._vbo);
    this._gl.deleteVertexArray(this._vao);
    this._shader.dispose();
  }

  private _direction(i: number, count: number): [number, number] {
    let dx: number, dz: number;
    if (i < count - 1) {
      dx = this._wxz[(i + 1) * 2] - this._wxz[i * 2];
      dz = this._wxz[(i + 1) * 2 + 1] - this._wxz[i * 2 + 1];
    } else {
      dx = this._wxz[i * 2] - this._wxz[(i - 1) * 2];
      dz = this._wxz[i * 2 + 1] - this._wxz[(i - 1) * 2 + 1];
    }
    const len = Math.sqrt(dx * dx + dz * dz);
    return len > 1e-6 ? [dx / len, dz / len] : [0, 1];
  }

  private _writeVert(
    idx: number, x: number, y: number, z: number, dist: number,
    c: readonly number[], warp: boolean, edge: number,
  ): void {
    const o = idx * FPV;
    this._buf[o] = x; this._buf[o + 1] = y; this._buf[o + 2] = z;
    this._buf[o + 3] = dist;
    this._buf[o + 4] = c[0]; this._buf[o + 5] = c[1]; this._buf[o + 6] = c[2];
    this._buf[o + 7] = warp ? 1.0 : 0.0;
    this._buf[o + 8] = edge;
  }

  private _attr(name: string, size: number, stride: number, offset: number): void {
    const loc = this._shader.getAttrib(name);
    if (loc < 0) return;
    const gl = this._gl;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offset);
  }
}
